/**
 * How formally a decision was made — a derived rung, read from the
 * decision's Evidence (#15565, ADR-0134 § 3).
 *
 * | Rung       | Who can produce it                      | Recorded as                                  |
 * |------------|-----------------------------------------|----------------------------------------------|
 * | `floated`  | the note taker; anyone framing it       | `commitmentLevel: proposed`                  |
 * | `stated`   | only the note taker                     | `commitmentLevel: decided`                   |
 * | `leaning`  | a facilitator or the decider            | MANUAL `kind: decision_leaning`              |
 * | `agreed`   | a facilitator, or the live resolve      | MANUAL `kind: decision_agreed`               |
 * | `ratified` | only the accountable decider            | MANUAL `kind: decision_ratification`         |
 * | `unknown`  | —                                       | no Evidence, or a pre-#14376 NULL level      |
 *
 * NEVER STORED. A formality column on `Belief` fails the admission test at
 * "atomic": it summarises several acts by different people, each of which
 * already has its own actor and quote. Nor is it ever folded into
 * `confidence`.
 *
 * THE NOTE TAKER'S CEILING IS `stated` — "decision language heard". A
 * transcript cannot see assent, authority, or what was settled in the
 * hallway, so a cue-gated quote never reads as "agreed". The per-quote
 * `COMMITMENT_LEVELS` vocabulary is MAPPED onto the ladder, not widened, and
 * the extraction schema still cannot express a human rung. As a second line
 * of defence a human-rung kind only counts on non-`INFERRED` Evidence, so
 * no extraction alone can ever render `leaning` or above.
 *
 * CLIENT-SAFE: zod only — no Prisma, no `server-only`.
 */

import { z } from 'zod'

import {
  DECISION_AGREED_EVIDENCE_KIND,
  DECISION_LEANING_EVIDENCE_KIND,
  DECISION_RATIFICATION_EVIDENCE_KIND,
} from '@/lib/evidence-kind-vocabulary'
import { DEFAULT_BELIEF_EVIDENCE_STANCE } from '@/lib/graph-consolidation/vocabulary'

/** The closed ladder, weakest first; `unknown` is no rung at all. */
export const DECISION_FORMALITY = [
  'floated',
  'stated',
  'leaning',
  'agreed',
  'ratified',
  'unknown',
] as const

/** The validator half of {@link DECISION_FORMALITY} (ADR-0107). */
export const decisionFormalitySchema = z.enum(DECISION_FORMALITY)

export type DecisionFormality = (typeof DECISION_FORMALITY)[number]

/** The highest rung the note taker can reach on its own. */
export const NOTETAKER_FORMALITY_CEILING =
  'stated' as const satisfies DecisionFormality

const RANK: Readonly<Record<DecisionFormality, number>> = {
  unknown: 0,
  floated: 1,
  stated: 2,
  leaning: 3,
  agreed: 4,
  ratified: 5,
}

/** Where a rung sits on the ladder; `unknown` is below every rung. */
export function formalityRank(formality: DecisionFormality): number {
  return RANK[formality]
}

/**
 * The per-quote `commitmentLevel` read onto the ladder: `proposed` as
 * `floated`, `decided` as `stated`. `assigned` is unreachable for a DECISION
 * (`extract-items-fold.ts`), so it — like NULL and anything outside the
 * vocabulary — claims no rung.
 */
export function formalityFromCommitmentLevel(
  level: string | null,
): 'floated' | 'stated' | 'unknown' {
  if (level === 'proposed') return 'floated'
  if (level === 'decided') return 'stated'
  return 'unknown'
}

/** The human acts, by the Evidence kind each is recorded as. */
const HUMAN_RUNG_BY_KIND: Readonly<Record<string, DecisionFormality>> = {
  [DECISION_LEANING_EVIDENCE_KIND]: 'leaning',
  [DECISION_AGREED_EVIDENCE_KIND]: 'agreed',
  [DECISION_RATIFICATION_EVIDENCE_KIND]: 'ratified',
}

/** Who produced the winning row. */
export type FormalityActor = {
  /** The person who acted, or null when nobody named did (the note taker). */
  personId: string | null
  /** Their display name, when the reader could resolve one. */
  name: string | null
  /** True when a machine read this from a transcript. */
  notetaker: boolean
  /**
   * True when a machine derived this from other Evidence (`AGGREGATED`),
   * so no person and no transcript stands behind it.
   */
  derived?: boolean
}

/**
 * One Evidence row backing a decision, as the read needs it. `stance` is
 * the `BeliefEvidence` edge's, not the Evidence's.
 */
export type FormalityEvidenceRow = {
  id: string
  kind: string
  /** `Evidence.source` — `MANUAL`, `INFERRED`, `AGGREGATED`. */
  source: string
  commitmentLevel: string | null
  stance: string
  dismissedAt: Date | null
  excerpt: string | null
  actor: FormalityActor
}

/** The derived rung, and the row that won it. */
export type DecisionFormalityRead = {
  formality: DecisionFormality
  evidenceId: string | null
  actor: FormalityActor | null
  excerpt: string | null
}

const NO_FORMALITY: DecisionFormalityRead = {
  formality: 'unknown',
  evidenceId: null,
  actor: null,
  excerpt: null,
}

/** The rung one row claims, before the live/stance filter. */
function rungOf(row: FormalityEvidenceRow): DecisionFormality {
  const human = Object.prototype.hasOwnProperty.call(
    HUMAN_RUNG_BY_KIND,
    row.kind,
  )
    ? HUMAN_RUNG_BY_KIND[row.kind]
    : undefined
  // A human act needs a human: only a MANUAL row can claim one, whatever
  // its kind says. INFERRED (extraction) and AGGREGATED (a derivation pass)
  // are machine readings, so neither ever renders above `stated`.
  if (human !== undefined && row.source === 'MANUAL') return human
  return formalityFromCommitmentLevel(row.commitmentLevel)
}

/**
 * The highest rung over the decision's live, non-dismissed,
 * `SUPPORTS`-stance Evidence, with the winning row's id, actor and excerpt.
 * Of equal rungs the first in the caller's order wins. No row with a rung
 * reads as `unknown` with no winner.
 */
export function formalityFromEvidence(
  rows: readonly FormalityEvidenceRow[],
): DecisionFormalityRead {
  let best: { row: FormalityEvidenceRow; rung: DecisionFormality } | null = null
  for (const row of rows) {
    if (row.dismissedAt !== null) continue
    if (row.stance !== DEFAULT_BELIEF_EVIDENCE_STANCE) continue
    const rung = rungOf(row)
    if (rung === 'unknown') continue
    if (best === null || RANK[rung] > RANK[best.rung]) best = { row, rung }
  }
  if (best === null) return NO_FORMALITY
  return {
    formality: best.rung,
    evidenceId: best.row.id,
    actor: best.row.actor,
    excerpt: best.row.excerpt,
  }
}

const LABELS: Readonly<Record<DecisionFormality, string>> = {
  floated: 'Floated',
  // Never "agreed": the note taker heard words, not the room's assent.
  stated: 'Decision language heard',
  leaning: 'Leaning',
  agreed: 'Agreed',
  ratified: 'Ratified',
  unknown: 'Formality unknown',
}

/** The words a surface shows for a rung. */
export function decisionFormalityLabel(formality: DecisionFormality): string {
  return LABELS[formality]
}

/** One sentence on what the rung means, for the disclosure under it. */
const MEANINGS: Readonly<Record<DecisionFormality, string>> = {
  floated: 'Raised, with no direction yet.',
  stated:
    'The note taker heard decision language in this quote. Nobody has confirmed the room agreed.',
  leaning: 'The room or the decider favours a direction; not settled yet.',
  agreed: 'A person confirmed the room agreed.',
  ratified: 'The accountable decider owns this call.',
  unknown: 'Nothing recorded says how formally this was decided.',
}

export function decisionFormalityMeaning(formality: DecisionFormality): string {
  return MEANINGS[formality]
}

/** Who produced the winning row, in words. */
export function formalityActorLabel(actor: FormalityActor): string {
  if (actor.notetaker) return 'Heard by the note taker'
  if (actor.derived === true) {
    return 'Derived automatically, not recorded by a person'
  }
  return actor.name === null
    ? 'Recorded by a person'
    : `Recorded by ${actor.name}`
}
